import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, Youtube, Instagram, Facebook, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#050505] pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Get In Touch</span>
            <h1 className="font-heading text-6xl md:text-8xl text-white tracking-tight mt-4 mb-6">
              CONTACT
              <span className="block text-gold-gradient">US</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              Have a project in mind? Looking to collaborate? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-white tracking-wider mb-8">
                LET'S CREATE TOGETHER
              </h2>
              <p className="text-[#A3A3A3] leading-relaxed mb-12">
                Whether you're an actor looking for opportunities, a collaborator with a story to tell, 
                or simply a fan of our work, we welcome your messages. Reach out to us and let's create 
                something extraordinary together.
              </p>

              {/* Contact Details */}
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E50914]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#E50914]" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <a
                      href="mailto:swarnalientertainment.social@gmail.com"
                      className="text-[#D4AF37] hover:underline break-all text-sm md:text-base"
                    >
                      swarnalientertainment.social@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E50914]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#E50914]" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Location</h3>
                    <p className="text-[#A3A3A3]">Kolkata, West Bengal, India</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12">
                <h3 className="text-white font-semibold mb-4">Follow Us</h3>
                <div className="flex items-center gap-4">
                  <a
                    href="https://www.youtube.com/@SwarnaliEntertainment/featured"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-[#A3A3A3] hover:bg-[#E50914] hover:text-white transition-all"
                    data-testid="contact-youtube"
                  >
                    <Youtube size={20} />
                  </a>
                  <a
                    href="https://www.instagram.com/swarnalientertainment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-[#A3A3A3] hover:bg-[#E50914] hover:text-white transition-all"
                    data-testid="contact-instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://www.facebook.com/swarnalientertainment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-[#A3A3A3] hover:bg-[#E50914] hover:text-white transition-all"
                    data-testid="contact-facebook"
                  >
                    <Facebook size={20} />
                  </a>
                </div>
              </div>

              {/* Producer Info */}
              <div className="mt-12 p-6 bg-[#121212] border border-white/5 rounded-xl">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">Founder & Producer</p>
                <h3 className="text-white font-semibold text-lg">Swarnali Chakraborty Acharya</h3>
                <p className="text-[#D4AF37] font-accent italic text-sm">Swarnali Entertainment</p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {isSubmitted ? (
                <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
                  <CheckCircle className="text-green-500 mx-auto mb-6" size={64} />
                  <h3 className="font-heading text-3xl text-white tracking-wider mb-4">
                    MESSAGE SENT!
                  </h3>
                  <p className="text-[#A3A3A3] mb-6">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-[#121212] border border-white/5 rounded-xl p-8 md:p-12 contact-form"
                  data-testid="contact-form"
                >
                  <h3 className="font-heading text-2xl text-white tracking-wider mb-8">
                    SEND US A MESSAGE
                  </h3>

                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-white mb-2 block">
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your name"
                        className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#E50914]"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-white mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter your email"
                        className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#E50914]"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-white mb-2 block">
                        Subject *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="What's this about?"
                        className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#E50914]"
                        data-testid="contact-subject-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-white mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your inquiry..."
                        rows={6}
                        className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-[#A3A3A3]/50 focus:border-[#E50914] resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#E50914] hover:bg-[#B20710] text-white py-6 text-sm tracking-widest uppercase font-semibold rounded-sm disabled:opacity-50"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="mr-2" size={18} />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mb-4">
              BASED IN KOLKATA
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-12">
              The cultural capital of India
            </p>

            <div className="aspect-[21/9] bg-[#121212] rounded-xl overflow-hidden relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="text-[#E50914] mx-auto mb-4" size={48} />
                  <p className="text-white font-heading text-2xl tracking-wider">KOLKATA</p>
                  <p className="text-[#D4AF37] font-accent italic">West Bengal, India</p>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-[#E50914]/10 rounded-full blur-2xl" />
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
